import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { Outlet, createFileRoute, useRouterState } from "@tanstack/react-router";
import { Pencil } from "lucide-react";

import { MapPanel } from "@/components/dashboard/map/map-panel";
import { MapSurface } from "@/components/dashboard/map/map-surface";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { PageHeader } from "@/components/dashboard/page-header";
import { StatusPill } from "@/components/dashboard/status-pill";
import { AssignGuards } from "@/components/zones/assign-guards";
import { RemoveMember } from "@/components/zones/remove-member";
import { ZoneRowActions } from "@/components/zones/row-actions";
import { common, nav, zones as copy } from "@/content";
import { can } from "@/constants/permissions";
import { ringCentre } from "@/utils/geo";
import { getZone, listAssignableGuards } from "@/api/zones";
import { useQuery } from "@tanstack/react-query";
import { useFmt } from "@/hooks/shared/useFmt";
import { useSession } from "@/context/UserContext";
import { PageError, PageLoading } from "@/components/dashboard/page-state";
import { RecordNotFound } from "@/components/dashboard/record-not-found";

/**
 * One zone: its boundary, its policy, and who is posted to it.
 *
 * `$id/edit` is a child of this route. Render `<Outlet />` when editing so the
 * form mounts instead of this detail view.
 */
export const Route = createFileRoute("/_main/dashboard/zones/$id")({
  component: () => (
    <RequirePermission action="zones.read">
      <ZoneIdLayout />
    </RequirePermission>
  ),
});

function ZoneIdLayout() {
  const showingChild = useRouterState({
    select: (state) => state.matches.some((match) => match.routeId.endsWith("/edit")),
  });
  if (showingChild) return <Outlet />;
  return <ZoneDetailPage />;
}

function ZoneDetailPage() {
  const { user, settings } = useSession();
  const fmt = useFmt();
  const { id } = Route.useParams();
  const zoneId = Number(id);
  const validId = Number.isInteger(zoneId) && zoneId >= 1;

  const canAssign = can(user.role, "zones.assignGuards");

  const zoneQuery = useQuery({
    queryKey: ["zones", "get", zoneId],
    queryFn: () => getZone(zoneId),
    enabled: validId,
  });

  /* Only fetched for somebody who can act on it. A reader without
     `zones.assignGuards` gets no assign panel, so the candidate list would be a
     request whose answer is never rendered. */
  const candidatesQuery = useQuery({
    queryKey: ["zones", "assignable", zoneId],
    queryFn: () => listAssignableGuards(zoneId),
    enabled: validId && canAssign,
  });

  if (!validId || (zoneQuery.isFetched && !zoneQuery.data)) {
    return <RecordNotFound backTo="/dashboard/zones" backLabel={copy.backToZones} />;
  }
  if (zoneQuery.isPending) return <PageLoading />;
  if (zoneQuery.isError || !zoneQuery.data) return <PageError error={zoneQuery.error} />;

  const zone = zoneQuery.data;
  const candidates = candidatesQuery.data ?? [];
  /* The org default, already in the session — used when the zone has no geometry
     to centre on. */
  const fallbackCentre = { lat: settings.defaultMapLat, lng: settings.defaultMapLng };

  const canWrite = can(user.role, "zones.write");
  const canArchive = can(user.role, "zones.archive");

  const centre = zone.polygon.length > 0 ? ringCentre(zone.polygon) : fallbackCentre;
  const understaffed = zone.guardCount < zone.requiredGuards && !zone.archivedAt;

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.operations}
        title={zone.name}
        lead={zone.notes ?? undefined}
        actions={
          <div className="flex items-center gap-2">
            {canWrite && !zone.archivedAt ? (
              <Link
                to={`/dashboard/zones/${zone.id}/edit`}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-n4 bg-n0 px-4 text-sm font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
              >
                <Pencil aria-hidden className="size-4" strokeWidth={1.6} />
                {copy.edit}
              </Link>
            ) : null}
            {canWrite || canArchive ? (
              <ZoneRowActions
                id={zone.id}
                name={zone.name}
                archived={zone.archivedAt !== null}
                memberCount={zone.members.length}
                unfencedIfArchived={zone.unfencedIfArchived}
                numerals={user.numeralSystem}
                canWrite={canWrite}
                canArchive={canArchive}
              />
            ) : null}
          </div>
        }
      />

      {/* `items-start` so each panel is as tall as its own content. Stretching
          them to a common height left the plan floating in 200px of nothing. */}
      <div className="mt-8 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <MapPanel title={copy.planTitle} className="min-w-0">
          <MapSurface
            zones={[
              {
                id: zone.id,
                name: zone.name,
                color: zone.color,
                polygon: zone.polygon,
                archived: zone.archivedAt !== null,
              },
            ]}
            centre={centre}
            numerals={user.numeralSystem}
          />
        </MapPanel>

        {/* A hairline grid, gap-as-border. The design system's sectioning device
            throughout the console — not a stack of cards. */}
        <dl className="grid h-fit gap-px overflow-hidden rounded-lg bg-n4 sm:grid-cols-2 lg:grid-cols-1">
          <Cell label={copy.columnPolicy}>
            {zone.geofencePolicy === "flag" ? (
              <>
                <StatusPill tone="attention">{copy.policyFlag}</StatusPill>
                <span className="mt-2 block text-[11px] leading-snug text-n6">
                  {copy.policyFlagHint}
                </span>
              </>
            ) : (
              <>
                <StatusPill tone="neutral">{copy.policyBlock}</StatusPill>
                <span className="mt-2 block text-[11px] leading-snug text-n6">
                  {copy.policyBlockHint}
                </span>
              </>
            )}
          </Cell>

          <Cell label={copy.columnGuards}>
            <span className="font-semibold">
              <span className="tabular">{fmt.count(zone.guardCount)}</span> {common.ofRequired}{" "}
              <span className="tabular">{fmt.count(zone.requiredGuards)}</span>
            </span>
            {understaffed ? (
              <span className="ms-2 align-middle">
                <StatusPill tone="attention">{copy.understaffed}</StatusPill>
              </span>
            ) : null}
          </Cell>

          <Cell label={copy.area}>
            <span className="tabular">{fmt.area(zone.areaSquareMetres)}</span>
            <span className="mt-1 block text-[11px] text-n6">
              {copy.vertexCount}: <span className="tabular">{fmt.count(zone.vertexCount)}</span>
            </span>
          </Cell>

          <Cell label={copy.centre}>
            <span className="tabular" dir="ltr">
              {centre.lat.toFixed(5)}, {centre.lng.toFixed(5)}
            </span>
          </Cell>

          <Cell label={copy.columnCreated}>
            <span className="tabular">{fmt.date(zone.createdAt)}</span>
            {zone.createdByName ? (
              <span className="mt-1 block text-[11px] text-n6">{zone.createdByName}</span>
            ) : null}
          </Cell>

          {zone.archivedAt ? (
            <Cell label={copy.archivedBadge}>
              <span className="tabular">{fmt.date(zone.archivedAt)}</span>
            </Cell>
          ) : null}
        </dl>
      </div>

      <section className="mt-10">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-n3 pb-4">
          <div>
            <h2 className="text-lg font-bold leading-head text-n9">{copy.membersTitle}</h2>
            <p className="mt-1 max-w-[60ch] text-xs leading-snug text-n6">{copy.membersLead}</p>
          </div>
        </div>

        {canAssign && !zone.archivedAt ? (
          <div className="mt-4">
            <AssignGuards
              zoneId={zone.id}
              candidates={candidates}
              numerals={user.numeralSystem}
            />
          </div>
        ) : null}

        {zone.members.length === 0 ? (
          <p className="mt-6 text-sm text-n6">{copy.membersEmpty}</p>
        ) : (
          <ul className="mt-5 flex flex-col gap-px overflow-hidden rounded-lg bg-n4">
            {zone.members.map((member) => (
              <li
                key={member.id}
                className="flex flex-wrap items-center gap-3 bg-n0 px-4 py-3.5"
              >
                <PersonAvatar name={member.name} photoUrl={member.photoUrl} />
                <Link
                  to={`/dashboard/employees/${member.id}`}
                  className="text-sm font-semibold text-n9 transition-colors duration-200 ease-brand hover:text-orange-700"
                >
                  {member.name}
                </Link>

                {/* The state that decides how removal is worded, so it is visible
                    before anyone opens the dialog. */}
                {member.active && member.zoneCount <= 1 ? (
                  <StatusPill tone="attention">{copy.onlyZone}</StatusPill>
                ) : null}
                {!member.active ? (
                  <StatusPill tone="negative">{copy.memberInactive}</StatusPill>
                ) : null}

                <span className="ms-auto flex items-center gap-3">
                  {member.supervisorName ? (
                    <span className="text-xs text-n6">{member.supervisorName}</span>
                  ) : null}
                  <span className="tabular text-xs text-n6">
                    {copy.memberSince} {fmt.dateNumeric(member.assignedAt)}
                  </span>
                  {canAssign && !zone.archivedAt ? (
                    <RemoveMember
                      zoneId={zone.id}
                      guardId={member.id}
                      guardName={member.name}
                      lastZone={member.active && member.zoneCount <= 1}
                    />
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Cell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="bg-n0 p-5">
      <dt className="text-[11px] font-semibold tracking-[0.04em] text-n6">{label}</dt>
      <dd className="mt-2 text-sm text-n8">{children}</dd>
    </div>
  );
}
